import { message } from "antd";
import { useState } from "react";
import styled from "styled-components";
import { SecuritiesTable } from "./components";
import { uniqueId } from "lodash";
import { CustomizeSmartfolio } from "./components/CustomizeSmartfolio/CustomizeSmartfolio";
import { useDispatch, useSelector } from "react-redux";
import {
  resetState,
  selectBasket,
  selectDescription,
  selectName,
} from "src/store/Goal-Based/SelfMadeSmartfolioSlice";
import { useBuildSmartfolioMutation } from "src/store/Goal-Based/goalBasedApi";
import {
  Button as UIButton,
  Modal as UIModal,
  Stepper,
} from "src/components/ui";
import { line, space } from "src/theme/ui";

const steps = [
  {
    title: "Add securities",
    /* What this step is FOR, said once at the top, rather than left for the
       reader to infer from a disabled Next button. */
    lead: "Pick at least two securities to build the smartfolio from.",
    content: SecuritiesTable,
    buttons: [
      {
        title: "Cancel",
      },
      {
        title: "Next",
        primary: true,
      },
    ],
  },
  {
    title: "Customise smartfolio",
    lead: "Name it, and set how much of it each security should be.",
    content: CustomizeSmartfolio,
    buttons: [
      {
        title: "Previous",
      },
      {
        title: "Build",
        primary: true,
      },
    ],
  },
] as const;

type ButtonsTitle = (typeof steps)[number]["buttons"][number]["title"];

/* The stepper sits in a band of its own above the body, ruled off from it —
   it is chrome, not content, and it should not read as the first row of the
   thing it is labelling. */
const StepsBand = styled.div`
  margin: -20px -20px ${space.lg}px;
  padding: 12px 20px;
  border-bottom: 1px solid ${line.hairline};
`;

export function SmartfolioBuilder({
  visible,
  setVisible,
}: {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const [current, setCurrent] = useState(0);
  const basket = useSelector(selectBasket);
  const name = useSelector(selectName);
  const description = useSelector(selectDescription);
  const dispatch = useDispatch<any>();
  const [buildSmartfolio, { isLoading }] = useBuildSmartfolioMutation();

  const Content = steps[current].content;

  const handlers: Record<ButtonsTitle, () => void> = {
    Next: () => {
      setCurrent((previous) => previous + 1);
    },
    Cancel: () => {
      setVisible(false);
      if (current === 0) dispatch(resetState());
    },
    Previous: () => {
      setCurrent((previous) => previous - 1);
    },
    Build: async () => {
      try {
        await buildSmartfolio({
          name,
          description,
          basket_items: basket.map(
            ({
              ticker_id,
              weight,
              minimum_bound_weight,
              maximum_bound_weight,
            }) => ({
              ticker_id,
              weight,
              minimum_bound_weight,
              maximum_bound_weight,
            }),
          ),
        }).unwrap();
        message.success("You have successfully created a new smartfolio!");
        dispatch(resetState());
      } catch (error) {
        message.error("Smartfolio creation failed!");
      }
      setCurrent(0);
      setVisible(false);
    },
  };

  return (
    <UIModal
      title="Build a smartfolio"
      description={steps[current].lead}
      size="xl"
      visible={visible}
      onCancel={handlers.Cancel}
      footer={steps[current].buttons.map(({ title, primary }) => (
        <UIButton
          key={title}
          variant={primary ? "primary" : "secondary"}
          size="default"
          onClick={async () => await handlers[title]()}
          loading={title === "Build" && isLoading}
          disabled={
            (title === "Next" && basket.length < 2) ||
            (title === "Build" &&
              (basket.length < 2 ||
                name.length < 5 ||
                description.length < 5 ||
                basket.some((security) => security.ticker_id < 0) ||
                basket.slice(1).some((security) => security.weight <= 0)))
          }
        >
          {title}
        </UIButton>
      ))}
    >
      <StepsBand>
        <Stepper
          current={current}
          steps={steps.map(({ title }) => ({ title, key: uniqueId() }))}
        />
      </StepsBand>
      <Content />
    </UIModal>
  );
}
